import React, { createContext, useContext, useState, useRef } from 'react';

const NotificationContext = createContext();

export const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotifications must be used within NotificationProvider');
    }
    return context;
};

export const NotificationProvider = ({ children }) => {
    const [notifications, setNotifications] = useState([]);
    const notificationIdRef = useRef(0);

    const addNotification = (notification) => {
        const id = `notification-${Date.now()}-${++notificationIdRef.current}`;
        const newNotification = {
            ...notification,
            id,
            dismissible: true,
            dismissLabel: 'Dismiss message',
            onDismiss: () => removeNotification(id),
        };
        setNotifications((prev) => [...prev, newNotification]);
    };

    const removeNotification = (id) => {
        setNotifications((prev) => prev.filter((n) => n.id !== id));
    };

    const addSuccess = (content, header) =>
        addNotification({
            type: 'success',
            content,
            ...(header && { header }),
        });

    const addError = (content, header) =>
        addNotification({
            type: 'error',
            content,
            header: header || 'Error',
        });

    const addWarning = (content, header) =>
        addNotification({
            type: 'warning',
            content,
            ...(header && { header }),
        });

    const addInfo = (content, header) =>
        addNotification({
            type: 'info',
            content,
            ...(header && { header }),
        });

    const addProgress = (content, header, progress = 0, details = null) => {
        const id = `notification-${Date.now()}-${++notificationIdRef.current}`;
        const newNotification = {
            type: 'in-progress',
            content,
            header,
            percentage: progress,
            details,
            id,
            dismissible: false,
        };
        setNotifications((prev) => [...prev, newNotification]);
        return id;
    };

    const updateProgress = (id, progress, content, details) => {
        setNotifications((prev) => 
            prev.map((n) => 
                n.id === id 
                    ? { 
                        ...n, 
                        ...(progress !== undefined && { percentage: progress }),
                        ...(content !== undefined && { content }),
                        ...(details !== undefined && { details })
                      }
                    : n
            )
        );
    };

    const completeProgress = (id, finalContent, details) => {
        setNotifications((prev) => 
            prev.map((n) => 
                n.id === id 
                    ? { 
                        ...n, 
                        type: 'success', 
                        content: finalContent,
                        ...(details !== undefined && { details }),
                        dismissible: true,
                        dismissLabel: 'Dismiss message',
                        onDismiss: () => removeNotification(id),
                        percentage: undefined
                    }
                    : n
            )
        );
    };

    return (
        <NotificationContext.Provider
            value={{
                notifications,
                addSuccess,
                addError,
                addWarning,
                addInfo,
                addProgress,
                updateProgress,
                completeProgress,
                removeNotification,
            }}
        >
            {children}
        </NotificationContext.Provider>
    );
};
